<script setup>
import getUuid from 'widget/helpers/uuid';
import { ref, onMounted, onUnmounted, defineEmits, defineExpose } from 'vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { format, intervalToDuration } from 'date-fns';
import { convertAudio } from './utils/mp3ConversionUtils';

const props = defineProps({
  audioRecordFormat: {
    type: String,
    required: true,
  },
});

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'pause',
  'play',
  'pauseStateChanged',
]);

const waveformContainer = ref(null);
const wavesurfer = ref(null);
const record = ref(null);
const isRecording = ref(false);
const isPlaying = ref(false);
const hasRecording = ref(false);
const isPaused = ref(false);
// Bandera para cancelar/descartar la grabación sin emitir el archivo (papelera).
let cancelled = false;

const formatTimeProgress = time => {
  const duration = intervalToDuration({ start: 0, end: time });
  return format(
    new Date(0, 0, 0, 0, duration.minutes, duration.seconds),
    'mm:ss'
  );
};

const initWaveSurfer = () => {
  wavesurfer.value = WaveSurfer.create({
    container: waveformContainer.value,
    // Onda dorada compacta (Propuesta 2 · M1.B · Victor 2026-06-02)
    waveColor: '#c9a35a',
    progressColor: '#8a6f33',
    height: 30,
    barWidth: 2.5,
    barGap: 2,
    barRadius: 2,
    plugins: [
      RecordPlugin.create({
        scrollingWaveform: true,
        renderRecordedAudio: false,
      }),
    ],
  });

  wavesurfer.value.on('pause', () => emit('pause'));
  wavesurfer.value.on('play', () => emit('play'));

  record.value = wavesurfer.value.plugins[0];

  wavesurfer.value.on('finish', () => {
    isPlaying.value = false;
  });

  record.value.on('record-end', async blob => {
    // Papelera: si se canceló, descartamos sin emitir el archivo.
    if (cancelled) {
      cancelled = false;
      isRecording.value = false;
      isPaused.value = false;
      return;
    }
    const audioUrl = URL.createObjectURL(blob);
    const audioBlob = await convertAudio(blob, props.audioRecordFormat);
    const fileName = `${getUuid()}.mp3`;
    const file = new File([audioBlob], fileName, {
      type: props.audioRecordFormat,
    });
    wavesurfer.value.load(audioUrl);
    emit('finishRecord', {
      name: file.name,
      type: file.type,
      size: file.size,
      file,
    });
    hasRecording.value = true;
    isRecording.value = false;
  });

  record.value.on('record-progress', time => {
    emit('recorderProgressChanged', formatTimeProgress(time));
  });
};

const stopRecording = () => {
  if (isRecording.value) {
    record.value.stopRecording();
    isRecording.value = false;
  }
};

const startRecording = () => {
  record.value.startRecording();
  isRecording.value = true;
};

// Pausa/reanuda DURANTE la grabación (Propuesta 2). Funciona por clic en
// celular, laptop y PC.
const pauseResume = () => {
  if (!record.value || !isRecording.value) return;
  if (record.value.isPaused()) {
    record.value.resumeRecording();
    isPaused.value = false;
  } else {
    record.value.pauseRecording();
    isPaused.value = true;
  }
  emit('pauseStateChanged', isPaused.value);
};

// Papelera: detiene y descarta la grabación (no emite archivo).
const cancelRecording = () => {
  cancelled = true;
  if (record.value && isRecording.value) {
    record.value.stopRecording();
  }
};

const playPause = () => {
  if (hasRecording.value) {
    wavesurfer.value.playPause();
    isPlaying.value = !isPlaying.value;
  }
};

onMounted(() => {
  initWaveSurfer();
  startRecording();
});

onUnmounted(() => {
  if (wavesurfer.value) {
    wavesurfer.value.destroy();
  }
});

defineExpose({ playPause, stopRecording, pauseResume, cancelRecording, record });
</script>

<template>
  <div ref="waveformContainer" class="w-full p-1" />
</template>
